import React from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const TYPE_COLORS = {
  'Distributor': 'bg-blue-100 text-blue-700',
  'Retail Store': 'bg-emerald-100 text-emerald-700',
  'Dealer': 'bg-amber-100 text-amber-700',
  'Sub-Distributor': 'bg-violet-100 text-violet-700',
};

const STATUS_COLORS = {
  'Active': 'bg-emerald-100 text-emerald-700',
  'Inactive': 'bg-slate-100 text-slate-600',
  'On Hold': 'bg-rose-100 text-rose-700',
};

export default function AccountDetailModal({ isOpen, account, onClose, onEdit, onMap }) {
  if (!isOpen || !account) return null;

  const InfoRow = ({ icon, label, value }) => (
    <div className="flex items-start gap-3 py-2 border-b border-border last:border-0">
      <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon name={icon} size={14} color="var(--color-primary)" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-caption text-xs text-muted-foreground">{label}</p>
        <p className="font-caption text-sm font-medium text-foreground break-words">{value || '—'}</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-base font-semibold text-foreground">{account?.name}</h2>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS?.[account?.type] || ''}`}>{account?.type}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS?.[account?.status] || ''}`}>{account?.status}</span>
            </div>
            <p className="font-caption text-xs text-muted-foreground mt-1">Account ID: #{account?.id}</p>
          </div>
          <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground transition-colors flex-shrink-0 ml-2" onClick={onClose}>
            <Icon name="X" size={18} color="currentColor" strokeWidth={2} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4 custom-scrollbar">
          {/* Map Preview */}
          {account?.lat && account?.lng && (
            <div className="h-40 rounded-lg overflow-hidden border border-border">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title={`Map location of ${account?.name}`}
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${account?.lat},${account?.lng}&z=14&output=embed`}
              />
            </div>
          )}

          {/* Sales Summary */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Last Sale', value: `₱${account?.lastSaleAmount?.toLocaleString()}`, icon: 'ShoppingCart' },
              { label: 'Credit Limit', value: `₱${account?.creditLimit?.toLocaleString()}`, icon: 'CreditCard' },
              { label: 'Credit Used', value: `${account?.creditUsedPct}%`, icon: 'TrendingUp' },
            ]?.map(s => (
              <div key={s?.label} className="bg-muted/40 rounded-lg p-3 text-center">
                <Icon name={s?.icon} size={16} color="var(--color-primary)" strokeWidth={2} className="mx-auto mb-1" />
                <p className="font-data text-sm font-semibold text-foreground">{s?.value}</p>
                <p className="font-caption text-xs text-muted-foreground">{s?.label}</p>
              </div>
            ))}
          </div>

          {/* Details */}
          <div>
            <InfoRow icon="MapPin" label="Address" value={`${account?.address}, ${account?.city || ''}, ${account?.province || ''} ${account?.zipCode || ''}`} />
            <InfoRow icon="Globe" label="Region / Area" value={`${account?.region} · ${account?.area}`} />
            <InfoRow icon="User" label="Assigned Salesperson" value={account?.salesperson} />
            <InfoRow icon="Phone" label="Contact Phone" value={account?.contactPhone} />
            <InfoRow icon="Mail" label="Contact Email" value={account?.contactEmail} />
            <InfoRow icon="Navigation" label="GPS Coordinates" value={account?.lat && account?.lng ? `${account?.lat}, ${account?.lng}` : null} />
            <InfoRow icon="Calendar" label="Last Sale Date" value={account?.lastSaleDate} />
            <InfoRow icon="FileText" label="Payment Terms" value={account?.paymentTerms} />
          </div>

          {/* Sales History */}
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground mb-2">Recent Sales History</h3>
            <div className="space-y-2">
              {(account?.salesHistory || [])?.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div>
                    <p className="font-caption text-sm text-foreground">{s?.date}</p>
                    <p className="font-caption text-xs text-muted-foreground">{s?.items} items · Order #{s?.orderId}</p>
                  </div>
                  <p className="font-data text-sm font-semibold text-foreground">₱{s?.amount?.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border flex-shrink-0">
          <Button variant="outline" iconName="MapPin" iconPosition="left" onClick={() => onMap(account)}>View on Map</Button>
          <Button variant="default" iconName="Pencil" iconPosition="left" onClick={() => { onClose(); onEdit(account); }}>Edit Account</Button>
        </div>
      </div>
    </div>
  );
}